import Spinner from "components/Spinner.jsx";
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { backendGet } from "../../services/dataService.js";
import { useUser } from "../../hooks/user.js";
import { Box, Typography } from "@mui/material";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import ObjectInfoCard from "../../components/ObjectInfoCard.jsx";
import { useBreadcrumb } from "../../contexts/BreadcrumbContext.jsx";
import BranchSelector from "../../components/BranchSelector.jsx";
import NewDamageClaimDialog from "../../components/NewDamageClaimDialog.jsx";
import { colorsFoundation, borderRadius } from "../../theme/index.js";
import { activeBrand } from "../../theme/brands/index.js";
import ClaimForm from "../../components/claim/ClaimForm.jsx";

const breadcrumbs = [
  { name: "Home", color: "secondary", path: "/home" },
  { name: "Schäden", color: "secondary", path: "/schaeden" },
  { name: "Neuer Schaden", color: "primary", route: "/schaden" },
];

export default function ClaimCreatePage(props) {
  const { setBreadcrumbs } = useBreadcrumb();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [selectedSparte, setSelectedSparte] = useState(null);
  const [debugInfo, setDebugInfo] = useState("");
  const [objectDialogOpen, setObjectDialogOpen] = useState(false);
  const { vertrag_id, objekt_id } = useParams();
  const { user, loading: userLoading } = useUser();
  const navigate = useNavigate();

  useEffect(() => {
    if (setBreadcrumbs) {
      setBreadcrumbs(breadcrumbs);
    }
  }, [setBreadcrumbs]);

  useEffect(() => {
    const getData = async () => {
      const endpoint = "data/schadenanlage/" + vertrag_id + "/" + objekt_id;
      await backendGet(endpoint, {
        setIsLoading,
        setResult: async (data) => {
          if (
            data.vertrag.letzter_ol_import === null &&
            data.sparten.length === 0
          ) {
            // setDebugInfo("*** Sparten wurden gemäß Excel-Vorgaben erzeugt ***");
            let sparten2set = [];
            switch (data.vertrag.vertragsart) {
              case "Bauherrenhaftpflicht":
              case "Berufshaftpflicht":
              case "Betreiber-Haftpflicht":
              case "Betreiberhaftpflicht":
              case "Betriebshaftpflicht":
              case "Gewässerschadenhaftpflicht":
              case "Haus- und Grundstückshaftpflicht":
              case "HV Privat":
              case "Privathaftpflicht":
              case "Privathaftpflicht für betreute Personen":
              case "Produkt-Rückrufkosten":
              case "Umwelthaftpflicht":
              case "Umweltschaden":
              case "Vermögensschaden":
              case "Vertrauensschaden":
                //I
                sparten2set = ["allgemeine Haftpflicht"];
                break;
              case "Bauleistung":
              case "Betriebsunterbrechung":
              case "Elektronik":
              case "Elektronik-BU":
              case "Gebäude":
              case "Gebäude/Inhalt":
              case "Gewerbe Gebäude":
              case "Hausrat":
              case "Hausrat mit Feuer":
              case "Inhalt":
              case "Maschinenbruch":
              case "Maschinenbruch-BU":
              case "Mietverlust":
              case "Privat Wohngebäude mit Feuer":
              case "Sach":
                //C,D,E,F,G,H
                sparten2set = [
                  "Gebäude (Feuer)",
                  "Inhalt (Feuer)",
                  "Leitungswasser",
                  "Einbruchdiebstahl",
                  "Sturm/Hagel",
                  "Elementar",
                  "Sonstige Gefahren (Gebäude)",
                  "Sonstige Gefahren (Inhalt)",
                ];
                break;
              case "BBB":
              case "Kasko":
              case "KFZ - Haftpflicht":
              case "KFZ - Haftpflicht (11 %)":
              case "KFZ - Haftpflicht (schwer)":
              case "KFZ - Schutzbrief":
              case "KFZ - Schutzbrief (11 %)":
              case "KFZ - Teilkasko":
              case "KFZ - Teilkasko (11 %)":
              case "KFZ - Teilkasko (schwer)":
              case "KFZ - Vollkasko / Teilkasko (11 %)":
              case "KFZ - Vollkasko / Teilkasko (schwer)":
              case "KFZ - Vollkasko/Teilkasko":
              case "KfZPolice-classic":
                //B
                sparten2set = ["Kfz-Schaden"];
                break;
              case "böswillige Beschädigung":
              case "Glas":
                //H
                sparten2set = [
                  "Sonstige Gefahren (Gebäude)",
                  "Sonstige Gefahren (Inhalt)",
                ];
                break;
              case "Dienstreisekasko":
                //A
                sparten2set = ["Dienstreisekasko"];
                break;
              case "EC":
              case "EC-Betriebsunterbrechungsversicherung":
              case "EC-Gebäude":
              case "EC-Inhalt":
              case "Gebäude (EC)":
              case "Inhalt (EC)":
              case "Mietverlust (EC)":
                //D,E,F,G,H
                sparten2set = [
                  "Leitungswasser",
                  "Einbruchdiebstahl",
                  "Sturm/Hagel",
                  "Elementar",
                  "Sonstige Gefahren (Gebäude)",
                  "Sonstige Gefahren (Inhalt)",
                ];
                break;
              case "Einbruchdiebstahl":
                //E
                sparten2set = ["Einbruchdiebstahl"];
                break;
              case "Sturm/Hagel":
                //F
                sparten2set = ["Sturm/Hagel"];
                break;
              case "Elementar":
                //G
                sparten2set = ["Elementar"];
                break;
              case "Feuer":
              case "Feuer-Betriebsunterbrechungsversicherung":
              case "Feuer-Gebäude":
              case "Feuer-Inhalt":
              case "Feuer-Rohbau":
              case "Gebäude (Feuer)":
              case "Inhalt (Feuer)":
              case "Mietverlust (Feuer)":
                //C
                sparten2set = ["Gebäude (Feuer)", "Inhalt (Feuer)"];
                break;
              case "Leitungswasser":
                //D
                sparten2set = ["Leitungswasser"];
                break;
              default:
                setDebugInfo(
                  "*** Fehler: Vertragsart '" +
                    data.vertrag.vertragsart +
                    "' ist nicht gemappt ***"
                );
            }
            data.sparten = sparten2set.map((item) => {
              return { sparte: item };
            });
          }
          setData(data);
          setIsLoading(false);
        },
      });
    };
    getData();
  }, []);

  const getFilteredSparten = (sparten) => {
    const filteredSparten = sparten
      .filter((sparte) => {
        return (
          sparte.sparte !== "GAP-Deckung" &&
          sparte.sparte !== "Sonstige Schäden"
        );
      })
      .sort((a, b) => {
        if (a.sparte < b.sparte) return -1;
        if (a.sparte > b.sparte) return 1;
        return 0;
      });
    return filteredSparten;
  };

  let bezeichnung = null;
  let sparteObj = null;
  if (data?.sparten && selectedSparte) {
    const sparte = data.sparten.find(
      (sparte) => sparte.sparte === selectedSparte
    );
    const obj = data?.objekt;
    if (obj.bezeichnung) bezeichnung = obj?.bezeichnung;
    if (obj.strasse) {
      bezeichnung = obj.strasse;
      if (obj.hausnr) bezeichnung += " " + obj.hausnr;
      if (obj.plz) bezeichnung += ", " + obj.plz;
      if (obj.ort) bezeichnung += " " + obj.ort;
    }
    sparteObj = sparte;
  }

  const handleEditSparte = () => {
    setSelectedSparte(null);
  };

  // Get display name for the current object
  const getObjectAndContractDisplayName = (object, contract) => {
    if (!object && !contract) {
      return " - unbekanntes Objekt / Vertrag -";
    }

    let object_name = "";

    if (object.bezeichnung) {
      object_name = object.bezeichnung;
    } else if (object.strasse) {
      object_name = object.strasse;
      if (object.hausnr) {
        object_name += " " + object.hausnr;
      }
    }

    let contract_name = contract.vertragsnummer;
    if (contract?.gesellschaft_name) {
      contract_name += ` (${contract.gesellschaft_name})`;
    }

    return object_name + " - " + contract_name;
  };

  // Handle object change from dialog
  const handleObjectChange = (object) => {
    navigate(`/schaeden/neu/${object.vertrag_id}/${object.id}`);
  };

  return (
    <>
      {isLoading || userLoading ? (
        <Spinner />
      ) : (
        <Box>
          {debugInfo}

          {!selectedSparte ? (
            <Box>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h1">Neuer Schaden</Typography>
              </Box>

              {/* Object/Contract Selector Input */}
              <Box sx={{ mb: 3 }}>
                <Box
                  onClick={() => setObjectDialogOpen(true)}
                  sx={{
                    position: "relative",
                    border: `1px solid ${colorsFoundation.neutral[300]}`,
                    borderRadius: borderRadius.xs,
                    backgroundColor: colorsFoundation.neutral.white,
                    cursor: "pointer",
                    "&:hover": {
                      borderColor: colorsFoundation.neutral[400],
                    },
                  }}
                >
                  {/* Floating label */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: "-10px",
                      left: "12px",
                      backgroundColor: colorsFoundation.neutral.white,
                      px: 0.5,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: colorsFoundation.neutral[700],
                      }}
                    >
                      Objekt / Vertrag
                    </Typography>
                  </Box>

                  {/* Input content */}
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      height: "56px",
                    }}
                  >
                    <Box sx={{ px: 1.5, py: 0.5, flex: 1 }}>
                      <Typography
                        sx={{
                          fontSize: "1rem",
                          color: colorsFoundation.neutral.softblack,
                        }}
                      >
                        {getObjectAndContractDisplayName(
                          data.objekt,
                          data.vertrag
                        )}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: "56px",
                        height: "100%",
                      }}
                    >
                      <SearchOutlinedIcon
                        sx={{ color: colorsFoundation.neutral[700] }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Box>

              <BranchSelector
                data={getFilteredSparten(data.sparten)}
                selectedSparte={selectedSparte}
                setSelectedSparte={setSelectedSparte}
              />
            </Box>
          ) : (
            (() => {
              const claimForm = (
                <ClaimForm
                  hideTitle={true}
                  model={{
                    vertragsnummer: data.vertrag.vertragsnummer,
                    gesellschaft: data.vertrag.gesellschaft_name,
                    vertrag_id_ov: data.vertrag.id_ov,
                    kunde_id_ov: data.vertrag.kunde_id_ov,
                    objekt_id: objekt_id,
                    sparte: selectedSparte,
                    sparteObj: sparteObj,
                    vorab_pruefung: false,
                    bezeichnung: bezeichnung,
                    user: user,
                  }}
                  useV2Layout={true}
                  sparte={selectedSparte}
                />
              );

              // The customer (schmidt) flow is a standalone guided wizard: no page
              // heading or object/status card, and it bleeds out to the content
              // edges so it fills the page like the click dummy.
              if (activeBrand.features?.claimWizardFlow === "customer") {
                return (
                  <Box
                    sx={{
                      mx: { xs: "-16px", md: "-32px", lg: "-40px" },
                      mt: "-20px",
                    }}
                  >
                    {claimForm}
                  </Box>
                );
              }

              return (
                <Box>
                  <Box>
                    <Typography variant="h1" sx={{ mb: 4 }}>
                      Schadenformular
                    </Typography>
                  </Box>
                  {data?.objekt && (
                    <ObjectInfoCard
                      object={data.objekt}
                      contract={data.vertrag}
                      branch={selectedSparte}
                      status={{ label: "Entwurf", value: "draft" }}
                      onEditBranch={handleEditSparte}
                    />
                  )}
                  {claimForm}
                </Box>
              );
            })()
          )}
        </Box>
      )}

      {/* Object Selection Dialog */}
      <NewDamageClaimDialog
        open={objectDialogOpen}
        onClose={() => setObjectDialogOpen(false)}
        onSelect={handleObjectChange}
        title="Objekt / Vertrag ändern"
      />
    </>
  );
}
